/**
 * Számlázz.hu Számla Agent: builds the invoice XML of a closed work order and reads the
 * answer (docs.szamlazz.hu/agent). Pure, no imports (also type checked and tested by Node in
 * packages/core/test/szamlazz.test.ts). Amounts come from the work order lines as stored
 * (net + VAT = gross exactly); the Agent checks unit price × quantity = net, so a line whose
 * net is not an exact multiple of its quantity is sent as one unit with the quantity in its name.
 */

export type PaymentMethod = "cash" | "card" | "transfer";

export interface InvoiceLine {
  description: string;
  quantity: number;
  unit: string | null;
  vatRate: number;
  net: number;
  vat: number;
  gross: number;
}

export interface InvoiceInput {
  agentKey: string;
  issueDate: string;
  fulfilDate: string;
  dueDate: string;
  method: PaymentMethod;
  paid: boolean;
  currency: string;
  language: "hu" | "en" | "de";
  comment: string;
  orderNumber: string;
  buyer: { name: string; postalCode: string; city: string; address: string; email: string | null; taxNumber: string | null; sendEmail: boolean };
  lines: InvoiceLine[];
}

const METHOD: Record<string, Record<PaymentMethod, string>> = {
  hu: { cash: "Készpénz", card: "Bankkártya", transfer: "Átutalás" },
  en: { cash: "Cash", card: "Bank card", transfer: "Bank transfer" },
  de: { cash: "Bargeld", card: "Bankkarte", transfer: "Überweisung" },
};

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
const el = (name: string, value: string | number | boolean | null | undefined) =>
  value === null || value === undefined || value === "" ? `<${name}/>` : `<${name}>${esc(String(value))}</${name}>`;

/** The lines as the Agent accepts them (exact unit prices). */
export function agentLines(lines: InvoiceLine[]): InvoiceLine[] {
  return lines.map((l) => {
    const unitNet = l.net / l.quantity;
    const exact = Math.abs(Math.round(unitNet * 100) / 100 * l.quantity - l.net) < 1e-9;
    if (exact) return l;
    const qty = Number.isInteger(l.quantity) ? String(l.quantity) : String(l.quantity).replace(".", ",");
    return { ...l, description: `${l.description} (${qty} ${l.unit ?? ""})`.replace(/\s+\)/, ")"), quantity: 1, unit: "db" };
  });
}

export function invoiceXml(p: InvoiceInput): string {
  const lines = agentLines(p.lines)
    .map((l) => {
      const unitNet = Math.round((l.net / l.quantity) * 100) / 100;
      return `<tetel>${el("megnevezes", l.description.slice(0, 250))}${el("mennyiseg", l.quantity)}${el("mennyisegiEgyseg", l.unit || "db")}${el("nettoEgysegar", unitNet)}${el("afakulcs", l.vatRate)}${el("nettoErtek", l.net)}${el("afaErtek", l.vat)}${el("bruttoErtek", l.gross)}</tetel>`;
    })
    .join("");
  const b = p.buyer;
  return `<?xml version="1.0" encoding="UTF-8"?>
<xmlszamla xmlns="http://www.szamlazz.hu/xmlszamla" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.szamlazz.hu/xmlszamla https://www.szamlazz.hu/szamla/docs/xsds/agent/xmlszamla.xsd">
<beallitasok>${el("szamlaagentkulcs", p.agentKey)}${el("eszamla", true)}${el("szamlaLetoltes", false)}</beallitasok>
<fejlec>${el("keltDatum", p.issueDate)}${el("teljesitesDatum", p.fulfilDate)}${el("fizetesiHataridoDatum", p.dueDate)}${el("fizmod", (METHOD[p.language] ?? METHOD.hu!)[p.method])}${el("penznem", p.currency)}${el("szamlaNyelve", p.language)}${el("megjegyzes", p.comment)}${el("rendelesSzam", p.orderNumber)}${el("fizetve", p.paid)}</fejlec>
<elado/>
<vevo>${el("nev", b.name)}${el("irsz", b.postalCode)}${el("telepules", b.city)}${el("cim", b.address)}${el("email", b.email)}${el("sendEmail", b.sendEmail && !!b.email)}${el("adoszam", b.taxNumber)}</vevo>
<tetelek>${lines}</tetelek>
</xmlszamla>`;
}

export interface CancelInput {
  agentKey: string;
  /** Number of the invoice to cancel. */
  number: string;
  issueDate: string;
  /** Fulfilment date of the cancelling invoice: the one of the original invoice. */
  fulfilDate: string;
  /** Why it is cancelled: kept in the program only, never sent to the Agent (owner's decision, 2026-10-31). */
  reason: string;
  /** The customer gets the cancelling invoice by e-mail when it is given. */
  buyerEmail: string | null;
}

/**
 * The Agent's reverse invoice request (form field "action-szamla_agent_st",
 * docs.szamlazz.hu/agent/reversing_invoice/xml): root xmlszamlast, elements in the XSD order.
 * The storno XSD has no sendEmail element; the buyer's e-mail is enough.
 */
export function cancelXml(p: CancelInput): string {
  const email = p.buyerEmail?.trim();
  return `<?xml version="1.0" encoding="UTF-8"?>
<xmlszamlast xmlns="http://www.szamlazz.hu/xmlszamlast" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.szamlazz.hu/xmlszamlast https://www.szamlazz.hu/szamla/docs/xsds/agentst/xmlszamlast.xsd">
<beallitasok>${el("szamlaagentkulcs", p.agentKey)}${el("eszamla", true)}${el("szamlaLetoltes", false)}</beallitasok>
<fejlec>${el("szamlaszam", p.number)}${el("keltDatum", p.issueDate)}${el("teljesitesDatum", p.fulfilDate)}${el("tipus", "SS")}</fejlec>
${email ? `<vevo>${el("email", email)}</vevo>` : ""}
</xmlszamlast>`;
}

/**
 * Decimals of the invoiced currencies (the same as CURRENCIES in packages/core/src/money.ts,
 * a test keeps them equal). Work order amounts are stored in minor units (HUF: forint,
 * EUR: cent); the Agent expects major units.
 */
export const CURRENCY_DECIMALS: Record<string, number> = { HUF: 0, EUR: 2, CZK: 2, PLN: 2, RON: 2, RSD: 2, UAH: 2 };
const decimalsOf = (currency: string) => CURRENCY_DECIMALS[currency] ?? 2;
export const minorToMajor = (minor: number, currency: string) => minor / 10 ** decimalsOf(currency);
export const majorToMinor = (major: number, currency: string) => Math.round(major * 10 ** decimalsOf(currency));

/** Stored lines (minor units) as the Agent takes them (major units). */
export function majorLines(lines: InvoiceLine[], currency: string): InvoiceLine[] {
  return lines.map((l) => ({ ...l, net: minorToMajor(l.net, currency), vat: minorToMajor(l.vat, currency), gross: minorToMajor(l.gross, currency) }));
}

/**
 * ok: issued. Not ok: definite = the Agent answered with an error, so no invoice was made;
 * otherwise the outcome is unknown (no clear answer) and must not be retried blindly.
 */
export type AgentAnswer =
  | { ok: true; number: string; gross: number | null; viewUrl: string | null }
  | { ok: false; definite: boolean; code: string; message: string };

/** Reads the Agent's answer (headers first, the [ERR] body as fallback). */
export function readAgentAnswer(headers: { get(name: string): string | null }, body: string): AgentAnswer {
  const dec = (s: string | null) => {
    if (!s) return null;
    try {
      return decodeURIComponent(s.replace(/\+/g, " "));
    } catch {
      return s;
    }
  };
  const code = headers.get("szlahu_error_code");
  if (code || body.startsWith("[ERR]")) return { ok: false, definite: true, code: code ?? "unknown", message: dec(headers.get("szlahu_error")) ?? body.slice(0, 300) };
  const number = dec(headers.get("szlahu_szamlaszam")) ?? body.match(/xmlagentresponse=DONE;(.+)/)?.[1]?.trim() ?? null;
  if (!number) return { ok: false, definite: false, code: "unknown", message: body.slice(0, 300) };
  const gross = headers.get("szlahu_bruttovegosszeg");
  return { ok: true, number, gross: gross ? Number(gross) : null, viewUrl: dec(headers.get("szlahu_vevoifiokurl")) };
}

/** What the customer's data still lacks for an invoice (Számlázz.hu needs a postal address). */
export function missingBuyerData(c: { name?: string | null; postalCode?: string | null; city?: string | null; address?: string | null }): string[] {
  const out: string[] = [];
  if (!c.name?.trim()) out.push("name");
  if (!c.postalCode?.trim()) out.push("postalCode");
  if (!c.city?.trim()) out.push("city");
  if (!c.address?.trim()) out.push("addressLine");
  return out;
}
